<template>
  <div class="">
    <!-- 父级选择器（已改为单选） -->
    <q-select v-model="selectedParent" clearable :readonly="readonly" :disable="disable" :options="filteredParents"
      use-input input-debounce="0" @filter="filterParents" dense :input-style="{ minWidth: '200px' }">
      <template v-slot:option="scope">
        <q-item v-bind="scope.itemProps">
          <q-item-section>
            <q-item-label>{{ scope.opt.label }}</q-item-label>
            <q-item-label caption v-if="showChildCount">
              包含 {{ getChildCount(scope.opt) }} 项
            </q-item-label>
          </q-item-section>
        </q-item>
      </template>
    </q-select>

    <!-- 子项列表 -->
    <div v-if="showChildrenSection" class="child-list-section q-mt-md">
      <div class="text-caption text-grey-7 q-mb-xs" v-if="childLabel">
        当前{{ childLabel }}列表
      </div>
      <q-list bordered class="rounded-borders scrollable-list">
        <template v-if="displayedChildren.length">
          <q-item v-for="child in displayedChildren" :key="child.value" class="child-item">
            <q-item-section>
              <q-item-label>{{ child.label }}</q-item-label>
              <q-item-label caption v-if="child.field_name">
                {{ child.field_name }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>

        <div v-else class="empty-state q-pa-md text-center text-grey">
          <q-icon name="info" size="sm" class="q-mr-xs" />
          没有可显示的项
        </div>
      </q-list>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  // 树形数据，格式为: [{value, label, children: [...]}, ...]
  data: {
    type: Array,
    default: () => [],
    required: true
  },
  // 父项标签文本（如："部门"）
  parentLabel: String,
  // 子项标签文本（如："员工"）
  childLabel: String,
  // 是否只读
  readonly: {
    type: Boolean,
    default: false
  },
  // 是否禁用
  disable: {
    type: Boolean,
    default: false
  },
  // 是否显示子项数量提示
  showChildCount: {
    type: Boolean,
    default: true
  },
  // 父组件传递的已选值
  selected: {
    type: [String, Number],
    default: null
  }
});

const emit = defineEmits(['update:selected']);

// 选中的父项（改为单选）
const selectedParent = ref(null);
// 所有父项数据
const allParents = ref([]);
// 过滤后的父项列表
const filteredParents = ref([]);

// 初始化数据
const initData = () => {
  // 修复：正确处理数组中的单个父项对象
  allParents.value = props.data.map(item => {
    // 如果数组元素已经是父项格式，直接使用
    if (item.children) {
      return {
        ...item,
        children: item.children || []
      };
    }
    // 否则尝试获取第一个元素
    const firstItem = item[0] || {};
    return {
      ...firstItem,
      children: firstItem.children || []
    };
  });

  filteredParents.value = [...allParents.value];

  // 根据父组件传入的selected值设置选中的父项
  if (props.selected) {
    const found = allParents.value.find(
      parent => parent.value === props.selected
    );
    selectedParent.value = found || null;
  } else {
    selectedParent.value = null;
  }
};

// 获取子项数量
const getChildCount = (parent) => {
  return parent.children ? parent.children.length : 0;
};

// 当前显示的子项（适配单选）
const displayedChildren = computed(() => {
  if (!selectedParent.value) return [];
  return selectedParent.value.children || [];
});

// 是否显示子项区域（适配单选）
const showChildrenSection = computed(() => {
  return !!selectedParent.value;
});

// 父项过滤
const filterParents = (val, update) => {
  update(() => {
    if (val === '') {
      filteredParents.value = allParents.value;
    } else {
      const needle = val.toLowerCase();
      filteredParents.value = allParents.value.filter(
        parent => parent.label && parent.label.toLowerCase().includes(needle)
      );
    }
  });
};

// 初始化组件
initData();

// 当传入数据变化时重新初始化
watch(() => props.data, () => {
  initData();
});

// 当selectedParent变化时，更新父组件的selected值
watch(selectedParent, (newVal) => {
  emit('update:selected', newVal ? newVal.value : null);
});

// 当父组件传入的selected值变化时，更新本地selectedParent
watch(() => props.selected, (newVal) => {
  if (!newVal) {
    selectedParent.value = null;
    return;
  }

  // 只有当当前选中的值与传入值不同时才更新
  if (!selectedParent.value || selectedParent.value.value !== newVal) {
    const found = allParents.value.find(
      parent => parent.value === newVal
    );
    selectedParent.value = found || null;
  }
});
</script>

<style scoped>
.scrollable-list {
  max-height: 300px;
  /* 设置最大高度 */
  overflow-y: auto;
  /* 添加垂直滚动条 */
}

.child-list-section {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 12px;
  background-color: #fafafa;
}

.child-item {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.child-item:last-child {
  border-bottom: none;
}

.empty-state {
  font-size: 0.9rem;
}
</style>